import { ticketType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import { Suspense, useMemo, useState } from "react"
import { AlertTriangle, Book, Edit, MoreHorizontal, Plus, Trash } from "react-feather"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import * as z from "zod"
import { type TicketModels, ticketApi } from "@/api/client/ticket"
import {
  formatTicketDate,
  getTicketStatusLabel,
  getTicketStatusVariant,
} from "@/api/client/ticket/utils"
import {
  TicketListWithOcAssignee,
  TicketListWithOwner,
  TicketListWithProperty,
  TicketListWithRoom,
  TicketListWithTenant,
  TicketListWithTicketMessages,
} from "@/api/generated/models"
import { ticketListQueryParams } from "@/api/generated/schemas/api.zod"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { SectionLoading } from "@/components/ui/loading"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { toast } from "@/hooks/use-toast"
import { openInNewTab } from "@/lib/navigation"

// 検索フォーム用のスキーマを拡張
const searchFormSchema = ticketListQueryParams
  .extend({
    status: z.string().optional(), // 単一のステータスを選択（空文字列は「すべて」）
    alertFrom: z.date().optional(),
    alertTo: z.date().optional(),
    isMessageRead: z.string().optional(),
    title: z.string().optional(),
    tenantName: z.string().optional(),
    propertyName: z.string().optional(),
    roomName: z.string().optional(),
    ownerName: z.string().optional(),
  })
  .omit({
    page: true,
    limit: true,
  })

type SearchFormValues = z.infer<typeof searchFormSchema>
type TicketListItem = TicketModels.TicketList200TicketsItem
type TicketMessage = NonNullable<TicketListItem["ticketMessages"]>[number]

// 文字列をDateに変換するヘルパー
const parseDate = (dateStr: string | null | undefined): Date | undefined => {
  if (!dateStr) return undefined
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? undefined : date
}

// DateをAPIフォーマット（YYYY-MM-DD）に変換するヘルパー
const formatDateForApi = (date: Date | undefined): string | null => {
  if (!date) return null
  return format(date, "yyyy-MM-dd")
}

const getLatestOwnerMessage = (ticketMessages?: TicketMessage[] | null) => {
  return (ticketMessages ?? [])
    .filter((message) => message.senderType === "owner" && !message.deletedAt)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
}

const formatUnreadChatDate = (ticket: TicketListItem) => {
  if (ticket.isMessageReadByAdmin !== false) {
    return null
  }

  const latestOwnerMessage = getLatestOwnerMessage(ticket.ticketMessages)
  if (!latestOwnerMessage) {
    return null
  }

  return format(new Date(latestOwnerMessage.createdAt), "yyyy-MM-dd HH:mm")
}

// nuqsのパーサー定義
const ticketPageParsers = {
  page: parseAsInteger.withDefault(1),
  status: parseAsString,
  ocAssigneeName: parseAsString,
  alertFrom: parseAsString,
  alertTo: parseAsString,
  isMessageRead: parseAsString,
  ticketId: parseAsInteger,
  title: parseAsString,
  tenantName: parseAsString,
  propertyName: parseAsString,
  roomName: parseAsString,
  ownerName: parseAsString,
}

export function TicketPage() {
  const itemsPerPage = 20
  const queryClient = useQueryClient()

  // URLクエリパラメーター管理
  const [queryParams, setQueryParams] = useQueryStates(ticketPageParsers)

  // フォームの値をクエリから復元
  const searchParams = useMemo(() => {
    if (!queryParams) return null
    const hasSearchParams = Object.entries(queryParams)
      .filter(([key]) => key !== "page")
      .some(
        ([_, value]) =>
          value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true)
      )

    if (!hasSearchParams) return null

    return {
      status: queryParams.status || "",
      ocAssigneeName: queryParams.ocAssigneeName || undefined,
      alertFrom: parseDate(queryParams.alertFrom),
      alertTo: parseDate(queryParams.alertTo),
      isMessageRead: queryParams.isMessageRead || "",
      ticketId: queryParams.ticketId || undefined,
      title: queryParams.title || "",
      tenantName: queryParams.tenantName || "",
      propertyName: queryParams.propertyName || "",
      roomName: queryParams.roomName || "",
      ownerName: queryParams.ownerName || "",
    } as SearchFormValues
  }, [queryParams])

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      status: queryParams.status || "",
      ocAssigneeName: queryParams.ocAssigneeName || undefined,
      alertFrom: parseDate(queryParams.alertFrom),
      alertTo: parseDate(queryParams.alertTo),
      isMessageRead: queryParams.isMessageRead || "",
      ticketId: queryParams.ticketId || undefined,
      title: queryParams.title || "",
      tenantName: queryParams.tenantName || "",
      propertyName: queryParams.propertyName || "",
      roomName: queryParams.roomName || "",
      ownerName: queryParams.ownerName || "",
    },
  })

  const onSubmit = (formData: SearchFormValues) => {
    // URLパラメーターを更新
    setQueryParams({
      page: 1, // 検索時はページを1にリセット
      status: formData.status || null,
      ocAssigneeName: formData.ocAssigneeName || null,
      alertFrom: formatDateForApi(formData.alertFrom),
      alertTo: formatDateForApi(formData.alertTo),
      isMessageRead: formData.isMessageRead || null,
      ticketId: formData.ticketId || null,
      title: formData.title || null,
      tenantName: formData.tenantName || null,
      propertyName: formData.propertyName || null,
      roomName: formData.roomName || null,
      ownerName: formData.ownerName || null,
    })
    queryClient.invalidateQueries({ queryKey: ["/tickets"] })
  }

  const onReset = () => {
    form.reset({
      status: "",
      ocAssigneeName: "",
      alertFrom: undefined,
      alertTo: undefined,
      isMessageRead: "",
      ticketId: undefined,
      title: "",
      tenantName: "",
      propertyName: "",
      roomName: "",
      ownerName: "",
    })
    // URLパラメーターをクリア
    setQueryParams({
      page: null,
      status: null,
      ocAssigneeName: null,
      alertFrom: null,
      alertTo: null,
      isMessageRead: null,
      ticketId: null,
      title: null,
      tenantName: null,
      propertyName: null,
      roomName: null,
      ownerName: null,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">案件一覧</h1>

      <div className="">
        <div className="p-4 border-b bg-white rounded-lg shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>状態（ステータス）</FormLabel>
                      <FormControl>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              value=""
                              checked={!field.value || field.value === ""}
                              onChange={() => field.onChange("")}
                              className="w-4 h-4"
                            />
                            <span>すべて</span>
                          </label>
                          {ticketType.TICKET_STATUSES.map((status) => (
                            <label key={status.key} className="flex items-center gap-2">
                              <input
                                type="radio"
                                value={status.key}
                                checked={field.value === status.key}
                                onChange={() => field.onChange(status.key)}
                                className="w-4 h-4"
                              />
                              <span>{status.value}</span>
                            </label>
                          ))}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="ocAssigneeName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OC担当者</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="担当者名"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="alertFrom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>アラート日 (From)</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="日付を選択"
                          className="h-[32px]"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="alertTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>アラート日 (To)</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="日付を選択"
                          className="h-[32px]"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isMessageRead"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>未読チャット</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          className="flex h-8 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm text-slate-600"
                        >
                          <option value="">すべて</option>
                          <option value="false">未読のみ</option>
                          <option value="true">既読のみ</option>
                        </select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="ticketId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>案件ID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder=""
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(e.target.value ? Number(e.target.value) : undefined)
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>案件タイトル</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tenantName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>賃借人名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>物件名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="roomName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>部屋番号</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ownerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>オーナー名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" variant="default">
                  検索
                </Button>
                <Button type="button" variant="outline" onClick={onReset}>
                  検索をリセット
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <ReactErrorBoundary>
          <Suspense fallback={<SectionLoading />}>
            <TicketPageContent
              searchParams={searchParams}
              currentPage={queryParams.page}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
            />
          </Suspense>
        </ReactErrorBoundary>
      </div>
    </div>
  )
}

interface TicketPageContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
}

function TicketPageContent({
  searchParams,
  currentPage,
  onPageChange,
  itemsPerPage,
}: TicketPageContentProps) {
  const navigate = useNavigate()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const { canCreate, canUpdate, canDelete } = usePermission()

  // APIリクエストパラメータを構築
  const apiParams = useMemo(() => {
    const params: TicketModels.TicketListParams = {
      page: currentPage,
      limit: itemsPerPage,
      withOwner: TicketListWithOwner.true,
      withProperty: TicketListWithProperty.true,
      withRoom: TicketListWithRoom.true,
      withTenant: TicketListWithTenant.true,
      withOcAssignee: TicketListWithOcAssignee.true,
      withTicketMessages: TicketListWithTicketMessages.true,
    }

    if (searchParams) {
      // ステータスフィルター（空文字列の場合はすべてのステータスを含める）
      if (searchParams.status && searchParams.status !== "") {
        params.status = searchParams.status as TicketModels.TicketListParams["status"]
      }

      // OC担当者フィルター
      if (searchParams.ocAssigneeName) {
        params.ocAssigneeName = searchParams.ocAssigneeName
      }

      // アラート日範囲フィルター
      if (searchParams.alertFrom) {
        params.alertFrom = format(searchParams.alertFrom, "yyyy-MM-dd")
      }
      if (searchParams.alertTo) {
        params.alertTo = format(searchParams.alertTo, "yyyy-MM-dd")
      }

      // 未読チャットフィルター
      if (searchParams.isMessageRead && searchParams.isMessageRead !== "") {
        params.isMessageRead =
          searchParams.isMessageRead as TicketModels.TicketListParams["isMessageRead"]
      }

      // 案件IDフィルター
      if (searchParams.ticketId) {
        params.ticketId = searchParams.ticketId
      }

      // タイトル部分一致フィルター
      if (searchParams.title) {
        params.title = searchParams.title
      }

      // 賃借人名部分一致フィルター
      if (searchParams.tenantName) {
        params.tenantName = searchParams.tenantName
      }

      // 物件名部分一致フィルター
      if (searchParams.propertyName) {
        params.propertyName = searchParams.propertyName
      }

      // オーナー名部分一致フィルター
      if (searchParams.ownerName) {
        params.ownerName = searchParams.ownerName
      }

      // 部屋番号部分一致フィルター
      if (searchParams.roomName) {
        params.roomName = searchParams.roomName
      }
    }

    return params
  }, [searchParams, currentPage, itemsPerPage])

  // API呼び出し
  const { data, refetch } = ticketApi.useTicketListSuspense(apiParams)

  // 削除用のMutation
  const deleteMutation = ticketApi.useTicketDelete({
    mutation: {
      onSuccess: () => {
        toast({
          title: "削除しました",
        })
        refetch()
      },
      onError: (error) => {
        toast({
          title: "削除に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        })
      },
    },
  })

  const handleDelete = (id: number) => {
    setDeleteTargetId(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (deleteTargetId !== null) {
      await deleteMutation.mutateAsync({ id: String(deleteTargetId) })
      setDeleteDialogOpen(false)
      setDeleteTargetId(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setDeleteTargetId(null)
  }

  const handleEdit = (id: number) => {
    navigate(`/tickets/edit/${id}`)
  }

  const handleDetail = (id: number) => {
    openInNewTab(`/tickets/${id}`)
  }

  const tickets = data?.tickets || []

  // ページネーション計算
  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / itemsPerPage)

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const halfRange = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={(e) => {
              e.preventDefault()
              handlePageChange(i)
            }}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }
    return pages
  }

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{totalCount}件の案件が見つかりました</div>
        {canCreate("tickets") && (
          <div>
            <Link to="/tickets/add">
              <Button>
                <Plus className="mr-2" size={16} />
                案件を追加
              </Button>
            </Link>
          </div>
        )}
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">案件ID</TableHead>
              <TableHead className="whitespace-nowrap min-w-[250px]">案件タイトル</TableHead>
              <TableHead className="whitespace-nowrap">賃借人名</TableHead>
              <TableHead className="whitespace-nowrap">物件名</TableHead>
              <TableHead className="whitespace-nowrap">部屋番号</TableHead>
              <TableHead className="whitespace-nowrap">オーナー名</TableHead>
              <TableHead className="whitespace-nowrap">ステータス</TableHead>
              <TableHead className="whitespace-nowrap">OC担当者</TableHead>
              <TableHead className="whitespace-nowrap">アラート日時</TableHead>
              <TableHead className="whitespace-nowrap">アラートコメント</TableHead>
              <TableHead className="whitespace-nowrap">未読チャット</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.map((ticket) => {
              const unreadChatDate = formatUnreadChatDate(ticket)

              return (
                <TableRow key={ticket.id}>
                  <TableCell>{ticket.id}</TableCell>
                  <TableCell className="text-blue-600 cursor-pointer hover:underline">
                    <Link to={`/tickets/${ticket.id}`} target="_blank" rel="noopener noreferrer">
                      {ticket.title}
                    </Link>
                  </TableCell>
                  <TableCell>{ticket.tenant?.name || "-"}</TableCell>
                  <TableCell>{ticket.property?.name || "-"}</TableCell>
                  <TableCell>{ticket.room?.name || "-"}</TableCell>
                  <TableCell>{ticket.owner?.name || "-"}</TableCell>
                  <TableCell>
                    <Label variant={getTicketStatusVariant(ticket.status)}>
                      {getTicketStatusLabel(ticket.status)}
                    </Label>
                  </TableCell>
                  <TableCell>{ticket.ocAssignee?.name || "-"}</TableCell>
                  <TableCell>{formatTicketDate(ticket.alertAt)}</TableCell>
                  <TableCell>{ticket.alertComment || "-"}</TableCell>
                  <TableCell>
                    {unreadChatDate ? (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500 text-white">
                          未読
                        </span>
                        <span className="text-sm text-slate-600">{unreadChatDate}</span>
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[200px]">
                        <DropdownMenuItem onClick={() => handleDetail(ticket.id)}>
                          <Book className="text-slate-600 mr-2" size={16} />
                          詳細
                        </DropdownMenuItem>
                        {canUpdate("tickets") && (
                          <DropdownMenuItem onClick={() => handleEdit(ticket.id)}>
                            <Edit className="text-slate-600 mr-2" size={16} />
                            編集
                          </DropdownMenuItem>
                        )}
                        {canDelete("tickets") && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(ticket.id)}
                              className="text-red-600"
                            >
                              <Trash className="mr-2" size={16} />
                              削除
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
            {tickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-8">
                  案件が見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-end">
          <div className="text-sm text-slate-500">
            {totalCount} 件中 {(currentPage - 1) * itemsPerPage + 1} -{" "}
            {Math.min(currentPage * itemsPerPage, totalCount)} 件を表示
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationFirst
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(1)
                  }}
                  disabled={currentPage === 1}
                  className="cursor-pointer"
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(currentPage - 1)
                  }}
                  disabled={currentPage === 1}
                  className="cursor-pointer"
                />
              </PaginationItem>
              {renderPageNumbers()}
              <PaginationItem>
                <PaginationNext
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(currentPage + 1)
                  }}
                  disabled={currentPage === totalPages}
                  className="cursor-pointer"
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLast
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(totalPages)
                  }}
                  disabled={currentPage === totalPages}
                  className="cursor-pointer"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-[463px] p-[30px] rounded-[15px]">
          <DialogHeader className="gap-5">
            <DialogTitle className="flex items-center gap-2.5 text-[22px] font-semibold text-slate-600 tracking-[-0.165px]">
              <AlertTriangle className="text-red-500" size={24} />
              本当に削除してよろしいですか？
            </DialogTitle>
            <DialogDescription className="text-sm font-medium text-slate-500 tracking-[-0.105px]">
              削除した案件は復元できません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 mt-2">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              loading={deleteMutation.isPending}
            >
              キャンセル
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleDeleteConfirm}
              loading={deleteMutation.isPending}
            >
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
